import React from 'react';

import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Button from '../../Shared/UI/Button/components/Button';
import ActionRow from '../../Shared/UI/ActionRow/components/ActionRow';
import Checkbox from '../../Shared/UI/Checkbox/components/Checkbox';

import { useArchivedJobProspectsState, useArchivedJobProspectsActions } from './ArchivedJobProspectsContext';
import { unarchiveJobProspect, bulkUnarchiveJobProspects } from './restApi';

const statusLabels = {
  awaiting_super_admin_approval: 'Awaiting Super Admin Approval',
  rejected_by_super_admin: 'Rejected By Super Admin',
  awaiting_admin_approval: 'Awaiting Admin Approval',
  rejected_by_admin: 'Rejected By Admin',
  not_started: 'Not Started',
  application_in_process: 'Application In Process',
  applied_and_waiting: 'Applied And Waiting',
  interviewing: 'Interviewing',
  offer_received: 'Offer Received',
  offer_accepted: 'Offer Accepted',
  not_hiring: 'Not Hiring',
  no_offer: 'No Offer',
  decided_against: 'Decided Against',
  waiting_for_decision: 'Waiting For Decision',
  closed: 'Closed',
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const ArchivedJobProspectsTable = () => {
  const state = useArchivedJobProspectsState();
  const { onResponse, onError, toggleJobProspectSelection, selectAllJobProspects, clearSelection } = useArchivedJobProspectsActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
  const selectedJobProspectIds = state?.selectedJobProspectIds || [];
  const archivedJobProspects = state?.archivedJobProspects || [];

  const handleUnarchiveJobProspect = (jobProspect) => {
    unarchiveJobProspect({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleBulkUnarchiveJobProspects = () => {
    bulkUnarchiveJobProspects({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectIds: selectedJobProspectIds,
      },
    });
  };

  const allIds = archivedJobProspects.map(jp => jp.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedJobProspectIds.includes(id));
  const someSelected = selectedJobProspectIds.length > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAllJobProspects(allIds);
    }
  };

  if (archivedJobProspects.length === 0) {
    return (
      <p style={{ textAlign: 'center', fontWeight: 'bold' }}>No archived job prospects available</p>
    );
  }

  return (
    <>
      {someSelected && (
        <ActionRow>
          <span>{selectedJobProspectIds.length} selected</span>
          <Button onClick={() => handleBulkUnarchiveJobProspects()}>
            Unarchive Selected
          </Button>
        </ActionRow>
      )}
      <Table>
        <Thead>
          <Tr>
            <Th>
              <Checkbox checked={allSelected} onChange={handleSelectAll} />
            </Th>
            <Th>Candidate</Th>
            <Th>Organization</Th>
            <Th>Job Description</Th>
            <Th>Original Status</Th>
            <Th>Archived At</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {archivedJobProspects.map(jobProspect => {
            const isSelected = selectedJobProspectIds.includes(jobProspect.id);

            return (
              <Tr key={jobProspect.id}>
                <Td>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleJobProspectSelection(jobProspect.id)}
                  />
                </Td>
                <Td>
                  {jobProspect.candidatePath && jobProspect.candidateName
                    ? <a href={jobProspect.candidatePath} target="_blank" rel="noopener noreferrer">{jobProspect.candidateName}</a>
                    : null
                  }
                </Td>
                <Td>
                  <a href={jobProspect.biopharmaGroupLink} target="_blank" rel="noopener noreferrer">{jobProspect.biopharmaGroupName}</a>
                </Td>
                <Td>
                  <a href={jobProspect.link} target="_blank" rel="noopener noreferrer">{jobProspect.title}</a>
                </Td>
                <Td>{statusLabels[jobProspect.status] || jobProspect.status}</Td>
                <Td>{formatDate(jobProspect.archivedAt)}</Td>
                <Td>
                  <ActionRow align="end">
                    <Button onClick={() => handleUnarchiveJobProspect(jobProspect)}>
                      Unarchive
                    </Button>
                  </ActionRow>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </>
  );
};

export default ArchivedJobProspectsTable;
